import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CircleAlert, Clock } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { timeAgo } from '@/utils/timeUtils'
import { useTranslation } from '@/i18n'
import type { RunListItem } from '../v4/contracts'
import { buildRunNotifications, type RunNotification } from './runLibrary'

/**
 * The library's bell over v4 runs (Figma "8. Edge Cases", Alert): the runs waiting on the
 * engineer (an ask, an approval, a parked step) and the runs that ended badly, newest first; a row
 * opens the run in the builder. The page hands it the list it already loaded.
 */
export default function RunNotificationsBell({ runs }: { runs: RunListItem[] }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const notifications = useMemo(() => buildRunNotifications(runs, t), [runs, t])
  const hasAny = notifications.needsReview.length > 0 || notifications.failedRuns.length > 0

  const row = (entry: RunNotification, tone: 'review' | 'failed') => (
    <button
      key={entry.runId}
      type="button"
      onClick={() => navigate(`/workflow/new?runId=${encodeURIComponent(entry.runId)}`)}
      className="flex w-full min-w-0 items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/60"
    >
      <span
        className={
          tone === 'review'
            ? 'mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-amber-300 text-amber-500 dark:border-amber-700'
            : 'mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-red-300 text-red-500 dark:border-red-800'
        }
      >
        {tone === 'review' ? <Clock className="size-4" /> : <CircleAlert className="size-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{entry.title}</span>
        <span className="block truncate text-xs text-muted-foreground">{entry.subtitle}</span>
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(entry.at)}</span>
    </button>
  )

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex size-5 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label={t('workflow:notifications', { defaultValue: 'Notifications' })}
        >
          <Bell className="size-4" />
          {notifications.needsReview.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-red-500" aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[420px] max-w-[90vw] p-0"
        aria-label={t('workflow:notifications', { defaultValue: 'Notifications' })}
      >
        {!hasAny ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('workflow:bell.empty', { defaultValue: 'Nothing needs your attention' })}
          </p>
        ) : (
          <div className="max-h-[400px] overflow-y-auto py-2">
            {notifications.needsReview.length > 0 && (
              <>
                <p className="px-4 pb-1 pt-2 text-sm text-muted-foreground">
                  {t('workflow:bell.needs_review', { defaultValue: 'Needs your review' })}
                </p>
                {notifications.needsReview.map((entry) => row(entry, 'review'))}
              </>
            )}
            {notifications.failedRuns.length > 0 && (
              <>
                <p className="px-4 pb-1 pt-2 text-sm text-muted-foreground">
                  {t('workflow:bell.failed_runs', { defaultValue: 'Failed runs' })}
                </p>
                {notifications.failedRuns.map((entry) => row(entry, 'failed'))}
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
